import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight, Search } from "lucide-react";
import api from "../api";
import { useLanguage } from "../i18n";
import { usePageMeta } from "../hooks/usePageMeta";
import { Reveal } from "../components/Reveal";

const TABS = ["local", "regional", "global"];

function Flag({ code }) {
  return (
    <img
      src={`https://flagcdn.com/w80/${code.toLowerCase()}.png`}
      alt={code}
      loading="lazy"
      className="w-11 h-11 rounded-full object-cover border-2 border-white shadow-sm shrink-0 group-hover:scale-105 transition-transform duration-200"
    />
  );
}

export default function Destinations() {
  usePageMeta("seo.catTitle", "seo.catDesc");
  const { t, regionName, fmt } = useLanguage();
  const [params, setParams] = useSearchParams();
  const tab = TABS.includes(params.get("type")) ? params.get("type") : "local";
  const [q, setQ] = useState(params.get("q") || "");
  const [items, setItems] = useState([]);
  const [mock, setMock] = useState(false);
  const [loading, setLoading] = useState(true);
  const [globalDest, setGlobalDest] = useState(null);

  useEffect(() => {
    api.get("/destinations?type=global").then(({ data }) => setGlobalDest((data.data || [])[0] || null)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    api.get(`/destinations?type=${tab}`).then(({ data }) => {
      setItems(data.data || []);
      setMock(!!data.meta?.mock);
    }).catch(() => setItems([])).finally(() => setLoading(false));
  }, [tab]);

  const filtered = useMemo(() => {
    if (!q.trim()) return items;
    const needle = q.trim().toLowerCase();
    return items.filter((d) => {
      const name = (d.type === "local" ? regionName(d.code) : d.name || "").toLowerCase();
      return name.includes(needle) || d.code.toLowerCase().includes(needle);
    });
  }, [items, q, regionName]);

  return (
    <div data-testid="destinations-page" className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-12 sm:py-16">
      <Reveal>
        <div className="font-mono-t text-[11px] font-bold uppercase tracking-[0.3em] text-[#FF6B35] mb-4">{t("featured.eyebrow")}</div>
        <h1 className="font-display font-extrabold tracking-tight text-4xl sm:text-5xl text-slate-900">{t("cat.title")}</h1>
        <p className="mt-4 text-slate-600 max-w-lg">{t("cat.sub")}</p>
      </Reveal>

      {mock && (
        <div data-testid="demo-catalog-banner" className="mt-8 rounded-2xl border border-amber-300/60 bg-amber-50 px-5 py-3 text-sm text-slate-600">
          <span className="font-mono-t text-[10px] font-bold uppercase tracking-[0.25em] text-[#FF6B35] mr-3">{t("misc.demo")}</span>
          {t("misc.demoSub")}
        </div>
      )}

      {tab === "local" && globalDest && (
        <Link to={`/destination/${globalDest.code}`} data-testid="global-promo"
          className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-3xl bg-[#0C0D0E] px-6 sm:px-8 py-6 group hover:bg-[#1C1B19] transition-colors">
          <div>
            <div className="font-mono-t text-[10px] font-bold uppercase tracking-[0.3em] text-[#FF6B35] mb-2">{t("dest.globalPromoKicker")}</div>
            <div className="font-display font-bold text-2xl text-white tracking-tight">{t("dest.globalPromoTitle")}</div>
            <div className="mt-1 text-sm text-white/60">{t("dest.globalPromoSub").replace("{n}", globalDest.countries || "")}</div>
          </div>
          <span className="inline-flex items-center gap-2 rounded-full bg-[#FF6B35] text-white px-6 py-3 font-mono-t text-[11px] font-bold uppercase tracking-widest group-hover:bg-white group-hover:text-[#0C0D0E] transition-colors">
            {t("dest.globalPromoCta")} →
          </span>
        </Link>
      )}

      <div data-testid="catalog-tabbar" className="sticky top-16 z-40 -mx-4 sm:-mx-6 lg:-mx-12 mt-10 px-4 sm:px-6 lg:px-12 py-3 bg-[#FBF9F5]/90 backdrop-blur-xl border-b border-slate-200/80 flex flex-col md:flex-row gap-4 md:items-center justify-between">
        <div className="flex gap-2" role="tablist">
          {TABS.map((k) => (
            <button key={k} data-testid={`catalog-tab-${k}`} role="tab" aria-selected={tab === k}
              onClick={() => setParams({ type: k })}
              className={`rounded-full px-5 py-2.5 font-mono-t text-xs font-bold uppercase tracking-[0.15em] transition-colors ${
                tab === k ? "bg-[#FF6B35] text-white shadow-md shadow-orange-200" : "bg-white border border-slate-200 text-slate-600 hover:border-[#FF6B35] hover:text-[#FF6B35]"
              }`}>
              {t(`cat.${k}`)}
            </button>
          ))}
        </div>
        <div className="flex max-w-sm flex-1 rounded-full border border-slate-200 bg-white shadow-sm overflow-hidden focus-within:border-[#FF6B35] transition-colors">
          <span className="pl-4 flex items-center text-slate-400"><Search className="w-4 h-4" /></span>
          <input
            data-testid="catalog-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("cat.searchPh")}
            className="flex-1 px-3 py-2.5 bg-transparent outline-none text-sm"
          />
        </div>
      </div>

      {loading ? (
        <div data-testid="catalog-loading" className="py-24 text-center font-mono-t text-xs font-bold uppercase tracking-[0.3em] text-slate-400">{t("misc.loading")}</div>
      ) : filtered.length === 0 ? (
        <div data-testid="catalog-empty" className="py-24 text-center text-slate-500">{t("cat.none")}</div>
      ) : (
        <div data-testid="destination-catalog-grid" className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {filtered.map((d, i) => (
            <Reveal key={d.code} delay={Math.min(i, 8) * 0.04}>
              <Link to={`/destination/${d.code}`} data-testid={`destination-card-${d.code}`}
                className="group block rounded-3xl bg-white border border-slate-200/80 p-6 shadow-sm hover:shadow-xl hover:shadow-orange-100 hover:-translate-y-1 hover:border-[#FF6B35]/40 transition-all duration-200 h-full">
                <div className="flex justify-between items-start">
                  {d.type === "local" ? (
                    <Flag code={d.code} />
                  ) : (
                    <span className="w-11 h-11 rounded-full bg-[#E6F7F5] text-[#0D9488] flex items-center justify-center font-display font-extrabold text-sm shrink-0">
                      {(d.name || "?").slice(0, 2).toUpperCase()}
                    </span>
                  )}
                  <span className="w-8 h-8 rounded-full bg-[#FFEDD5] text-[#FF6B35] flex items-center justify-center group-hover:bg-[#FF6B35] group-hover:text-white transition-colors">
                    <ArrowUpRight className="w-3.5 h-3.5" />
                  </span>
                </div>
                <h3 className="font-display font-bold text-lg mt-4 tracking-tight text-slate-900">
                  {d.type === "local" ? regionName(d.code) : d.name}
                </h3>
                {d.type !== "local" && d.countries && (
                  <span data-testid={`country-badge-${d.code}`}
                    className="mt-2 inline-block rounded-full bg-[#E6F7F5] text-[#0D9488] px-3 py-1 font-mono-t text-[10px] font-bold uppercase tracking-[0.15em]">
                    {d.countries} {t("dest.countries")}
                  </span>
                )}
                <div className="mt-3 flex items-center justify-between">
                  <span className="font-mono-t text-[11px] text-slate-400">{d.plans} {t("cat.plans")}</span>
                  <span className="font-mono-t text-sm font-bold text-[#FF6B35]">{t("cat.from")} {fmt(d.min_price)}</span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      )}
    </div>
  );
}
